import { useState } from 'react';
import { IItem, IPaginationComponentProps, IPaginationResult } from '../interfaces/IPagination';

const usePagination = <ItemType extends IItem>({
    page: initialPage,
    pageSize: initialPageSize,
    totalItems: initialTotalItems,
    sortOrder: initialSortOrder = 'desc'
}: IPaginationComponentProps): IPaginationResult<ItemType> => {
    const [data, setData] = useState<ItemType[]>([]);
    const [selectedItem, setSelectedItem] = useState<ItemType | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [page, setPage] = useState<number | undefined>(initialPage);
    const [pageSize, setPageSize] = useState<number | undefined>(initialPageSize);
    const [totalItems, setTotalItems] = useState<number>(initialTotalItems ?? 0);
    const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>(initialSortOrder ?? 'desc');

    // Alternar entre ascendente y descendente
    const toggleSortOrder = () => {
        setSortOrder((prevSortOrder) => (prevSortOrder === 'asc' ? 'desc' : 'asc'));
        // Volver a la primera página al cambiar la dirección del orden
        setPage(1);
    }

    // Cambiar paginación
    const changePage = (page: number, pageSize: number) => {
        setPage(page);
        setPageSize(pageSize);
    };

    const getElementById = (id: number | string) => {
        return data.find((item: ItemType) => item.id === id);
    }

    const addElement = (item: IItem) => {
        const exists = getElementById(item.id);
        if (exists) return;

        if (pageSize && page) {
            setData((prevState: ItemType[]) => {
                let newState = [...prevState];

                const isFirstPage = page === 1;
                const totalPages = Math.ceil(totalItems / pageSize);
                const isLastPage = page === totalPages;
                const hasMaxItems = prevState.length >= pageSize;

                const shouldAdd =
                    (sortOrder === 'desc' && isFirstPage) ||    // Si estamos en la primera página y el orden es descendente
                    (sortOrder === 'asc' && isLastPage);       // Si estamos en la última página y el orden es ascendente

                if (shouldAdd) {
                    if (hasMaxItems) {
                        newState = sortOrder === 'desc'
                            ? newState.slice(0, pageSize - 1) // Elimina el último
                            : newState.slice(1);              // Elimina el primero
                    }

                    newState = sortOrder === 'desc'
                        ? [item as ItemType, ...newState]   // Insertar al principio
                        : [...newState, item as ItemType];  // Insertar al final
                }

                return newState;
            });
        } else {
            setData((prevState: ItemType[]) => [item as ItemType, ...prevState]);
        }

        setTotalItems(prev => prev + 1);
    };


    // Actualizar elemento
    const updateElement = (item: ItemType) => {
        setData(prevState =>
            prevState.map(element =>
                (element.id === item.id ? item : element)
            )
        );
    }
    // Seleccionar elemento
    const selectItem = (item: ItemType) => {
        setSelectedItem(item);
    };

    // Limpiar selección
    const clearSelectedItem = () => {
        setSelectedItem(null);
    };

    const loadingStart = () => setLoading(true);
    const loadingEnd = () => setLoading(false);
    return {
        data,
        page,
        pageSize,
        totalItems,
        loading,
        setData,
        setPage,
        setPageSize,
        setTotalItems,
        loadingStart,
        loadingEnd,
        addElement,
        updateElement,
        changePage,
        sortOrder,
        toggleSortOrder,
        selectItem,
        selectedItem,
        clearSelectedItem,
        getElementById
    };
};

export default usePagination;
